import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@rocco/utils/cn';
import { useIsHomePage } from '../use-is-home-page';

// Fine-grained observer thresholds so the reveal callback fires as the element
// scrolls in even when it is taller than the viewport (its element-ratio then
// tops out low and would never cross a single high threshold).
const REVEAL_THRESHOLDS = Array.from({ length: 21 }, (_, step) => step * 0.05);

export interface ScrollRevealProps {
    children: ReactNode;
    /** Extra classes on the wrapper (it is a full-width block). */
    className?: string;
    /** Transition delay in ms, for staggering sibling reveals. */
    delay?: number;
    /**
     * Fraction of the element that must be in view before it reveals (the
     * IntersectionObserver threshold). Default 0.1. Raise it for short sections
     * (e.g. small-tile carousels) so they reveal once mostly on screen rather
     * than the instant an edge peeks in. Keep below 1 for blocks taller than the
     * viewport, which can never reach full visibility.
     */
    threshold?: number;
    /**
     * Fired once when the block reveals — when it crosses the threshold, or
     * immediately under reduced motion. Lets an owner defer work (e.g. starting a
     * carousel's autoplay) until the content is actually on screen.
     */
    onReveal?: () => void;
    /** Transition duration class (default `duration-700`). */
    durationClassName?: string;
    /** Hidden (pre-reveal) transform + opacity (default `translate-y-8 opacity-0`). */
    hiddenClassName?: string;
    /** Revealed transform + opacity (default `translate-y-0 opacity-100`). */
    revealClassName?: string;
    /**
     * Start already revealed and skip the observer entirely. Use for content that
     * should appear without animation (e.g. views restored from cache on Back,
     * where an extra fade would fight scroll restoration).
     */
    immediate?: boolean;
    /**
     * When false, render revealed from the first paint and never animate — but
     * unlike `immediate`, the observer still runs and `onReveal` still fires as
     * the block crosses the threshold, so owners deferring work (e.g. carousel
     * autoplay) keep their timing. For surfaces where the pre-reveal dim reads
     * as missing content (mobile detail pages, above-fold carousels).
     */
    animate?: boolean;
}

/**
 * Fades + slides a section up as it scrolls into view (desktop scroll-reveal).
 * Uses a one-shot IntersectionObserver + GPU transform/opacity transition, so
 * it's cheap and never re-triggers. Honours `prefers-reduced-motion` by showing
 * the content immediately with no movement. SSR renders the hidden state; the
 * observer reveals on hydration (in-view sections reveal on first paint).
 */
export const ScrollReveal = ({
    children,
    className,
    delay,
    threshold = 0.1,
    onReveal,
    durationClassName = 'duration-700',
    hiddenClassName = 'translate-y-8 opacity-0',
    revealClassName = 'translate-y-0 opacity-100',
    immediate = false,
    animate = true,
}: ScrollRevealProps) => {
    const isHomePage = useIsHomePage();
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(immediate || !animate);
    // Read through a ref so a fresh inline `onReveal` doesn't re-run the effect.
    const onRevealRef = useRef(onReveal);
    onRevealRef.current = onReveal;

    useEffect(() => {
        if (immediate) return;
        const element = ref.current;
        if (!element) return;

        // Focus and the observer can each get here first, so this is guarded
        // and tears the other trigger down on the way through: `onReveal` is a
        // one-shot, and callers (e.g. carousel autoplay) depend on that.
        let revealed = false;
        let observer: IntersectionObserver | undefined;

        const reveal = () => {
            if (revealed) return;
            revealed = true;
            element.removeEventListener('focusin', reveal);
            observer?.disconnect();
            setVisible(true);
            onRevealRef.current?.();
        };

        const prefersReduced = window.matchMedia(
            '(prefers-reduced-motion: reduce)',
        ).matches;
        if (prefersReduced) {
            reveal();
            return;
        }

        // Tabbing can reach a link inside a section the observer has not
        // revealed yet — the browser scrolls focus into view, but the block is
        // still at opacity 0, so a keyboard user would be sitting on content
        // they cannot see. Reveal on the way in.
        element.addEventListener('focusin', reveal);

        observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry?.isIntersecting) return;
                const viewportHeight =
                    entry.rootBounds?.height ?? window.innerHeight;
                // Reveal when `threshold` of the element is in view (short
                // blocks) OR when it already covers `threshold` of the viewport.
                // The second clause is purely additive — a block taller than the
                // viewport can never reach a high element-ratio, so without it a
                // `threshold` like 0.8 would leave it stuck at opacity 0.
                const coversViewport =
                    viewportHeight > 0 &&
                    entry.intersectionRect.height >= threshold * viewportHeight;
                if (entry.intersectionRatio >= threshold || coversViewport) {
                    reveal();
                }
            },
            { threshold: REVEAL_THRESHOLDS, rootMargin: '0px 0px -10% 0px' },
        );
        observer.observe(element);
        return () => {
            element.removeEventListener('focusin', reveal);
            observer?.disconnect();
        };
    }, [threshold, immediate]);

    return (
        <div
            ref={ref}
            className={cn(
                // `scroll-reveal` is a no-op style hook: a <noscript> rule in the
                // document head forces it visible when JS is off, so crawlers and
                // no-JS agents see the content (SSR paints the hidden state).
                'scroll-reveal w-full transform-gpu transition-[opacity,transform] ease-out motion-reduce:transition-none',
                durationClassName,
                visible ? revealClassName : hiddenClassName,
                // The desktop homepage shows its sections as they are, with
                // no fade in. In CSS rather than state, so the server and the
                // first client paint agree on it at every width.
                isHomePage &&
                    'md:translate-y-0 md:opacity-100 md:transition-none',
                className,
            )}
            style={
                delay && !immediate
                    ? { transitionDelay: `${delay}ms` }
                    : undefined
            }
        >
            {children}
        </div>
    );
};
